
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { BikeMovementWebhookPayload } from '@/types/webhookTypes';

export interface BulkMovementUpdate {
  movement_blocked?: boolean;
  speed_limit?: number | null;
  last_movement_update?: string;
}

export interface BulkOperationResult {
  success: boolean;
  updatedCount: number;
  failedCount: number;
  errors: string[];
}

/**
 * Функция для отправки вебхука для массовых операций
 */
const sendBulkMovementWebhook = async (
  bikeIds: string[],
  action: 'block' | 'unblock' | 'speed_limit' | 'reset',
  changes: any
): Promise<void> => {
  try {
    console.log('=== ОТПРАВКА МАССОВЫХ ВЕБХУКОВ ===');
    console.log('Велосипеды для вебхуков:', bikeIds);
    console.log('Действие:', action);
    console.log('Изменения:', changes);

    if (!bikeIds || bikeIds.length === 0) {
      console.log('Нет велосипедов для отправки вебхуков');
      return;
    }

    // Получаем информацию о текущем пользователе
    const { data: { user } } = await supabase.auth.getUser();
    console.log('Пользователь для вебхука:', user?.id);
    
    // Загружаем данные велосипедов простым запросом (как в одиночной операции)
    console.log('Загружаем данные велосипедов...');
    const { data: bikesData, error: bikesError } = await supabase
      .from('bikes')
      .select('*')
      .in('id', bikeIds);

    if (bikesError) {
      console.error('Ошибка загрузки данных велосипедов для вебхука:', bikesError);
      return;
    }

    if (!bikesData || bikesData.length === 0) {
      console.log('Не найдено данных велосипедов для вебхуков');
      return;
    }

    console.log(`Загружено ${bikesData.length} велосипедов для вебхуков`);

    // Отправляем вебхук для каждого велосипеда
    for (const bikeData of bikesData) {
      console.log(`Обрабатываем велосипед ${bikeData.id} для вебхука`);
      
      // Загружаем данные курьера отдельно (как в одиночной операции)
      let currentRental = null;
      
      if (bikeData.assigned_to) {
        // Ищем активную аренду для этого велосипеда
        const { data: rentalData } = await supabase
          .from('rentals')
          .select(`
            id,
            human_number,
            courier_id,
            status,
            couriers (
              id,
              name,
              phone
            )
          `)
          .eq('bike_id', bikeData.id)
          .in('status', ['active', 'pending', 'assigned'])
          .single();

        if (rentalData && rentalData.couriers && rentalData.couriers.length > 0) {
          const courier = rentalData.couriers[0];
          currentRental = {
            id: rentalData.id,
            courier_name: courier.name || 'Неизвестный курьер',
            human_number: rentalData.human_number || null,
            courier_phone: courier.phone || null,
          };
        }
      }
      
      console.log('Данные аренды для велосипеда:', currentRental?.id || 'нет');
      
      const webhookPayload: BikeMovementWebhookPayload = {
        event_type: 'bike_movement_control_change',
        action,
        timestamp: new Date().toISOString(),
        bike: {
          id: bikeData.id,
          serial_number: bikeData.serial_number || '',
          model: bikeData.model || '',
          gps_tag: bikeData.gps_tag || null,
          current_rental: currentRental,
        },
        changes,
        admin_info: {
          user_id: user?.id || 'unknown',
          timestamp: new Date().toISOString(),
        },
      };

      console.log(`Отправка вебхука для велосипеда ${bikeData.id}:`, JSON.stringify(webhookPayload, null, 2));

      // Вызываем Edge Function для отправки вебхука
      const { data, error } = await supabase.functions.invoke('movement-webhook', {
        body: webhookPayload,
      });

      if (error) {
        console.error(`Ошибка при отправке вебхука для велосипеда ${bikeData.id}:`, error);
      } else {
        console.log(`Вебхук для велосипеда ${bikeData.id} успешно отправлен:`, data);
      }
    }

    console.log('=== МАССОВЫЕ ВЕБХУКИ ЗАВЕРШЕНЫ ===');
  } catch (error) {
    console.error('Критическая ошибка при отправке массовых вебхуков:', error);
  }
};

/**
 * Выполняет массовое обновление параметров движения для велосипедов
 */
export const bulkUpdateBikeMovement = async (
  bikeIds: string[],
  updates: BulkMovementUpdate
): Promise<BulkOperationResult> => {
  try {
    console.log('Выполнение массового обновления для велосипедов:', bikeIds, updates);
    
    const updateData = {
      ...updates,
      last_movement_update: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('bikes')
      .update(updateData)
      .in('id', bikeIds)
      .select('id');

    if (error) throw error;

    const updatedCount = data?.length || 0;
    const failedCount = bikeIds.length - updatedCount;

    console.log('Массовое обновление завершено:', { updatedCount, failedCount });

    return {
      success: true,
      updatedCount,
      failedCount,
      errors: []
    };
  } catch (error) {
    console.error('Ошибка массового обновления:', error);
    return {
      success: false,
      updatedCount: 0,
      failedCount: bikeIds.length,
      errors: [error instanceof Error ? error.message : 'Неизвестная ошибка']
    };
  }
};

/**
 * Массовая блокировка велосипедов
 */
export const bulkBlockBikes = async (bikeIds: string[]): Promise<BulkOperationResult> => {
  console.log('=== МАССОВАЯ БЛОКИРОВКА ВЕЛОСИПЕДОВ ===');
  console.log('ID велосипедов для блокировки:', bikeIds);
  
  const result = await bulkUpdateBikeMovement(bikeIds, {
    movement_blocked: true,
    speed_limit: null
  });

  console.log('Результат массового обновления:', result);

  if (result.success && result.updatedCount > 0) {
    toast.success(`Заблокировано велосипедов: ${result.updatedCount}`);
    
    console.log('Отправляем вебхуки для заблокированных велосипедов...');
    // Отправляем вебхуки только для успешно обновленных велосипедов
    const updatedBikeIds = bikeIds.slice(0, result.updatedCount);
    await sendBulkMovementWebhook(updatedBikeIds, 'block', {
      movement_blocked: {
        previous: false,
        current: true
      },
      speed_limit: {
        previous: null,
        current: null
      }
    });
  } else {
    console.error('Ошибка при массовой блокировке:', result.errors);
    toast.error('Ошибка блокировки велосипедов');
  }

  return result;
};

/**
 * Массовая разблокировка велосипедов
 */
export const bulkUnblockBikes = async (bikeIds: string[]): Promise<BulkOperationResult> => {
  console.log('=== МАССОВАЯ РАЗБЛОКИРОВКА ВЕЛОСИПЕДОВ ===');
  console.log('ID велосипедов для разблокировки:', bikeIds);
  
  const result = await bulkUpdateBikeMovement(bikeIds, {
    movement_blocked: false,
    speed_limit: null
  });

  console.log('Результат массового обновления:', result);

  if (result.success && result.updatedCount > 0) {
    toast.success(`Разблокировано велосипедов: ${result.updatedCount}`);
    
    console.log('Отправляем вебхуки для разблокированных велосипедов...');
    // Отправляем вебхуки только для успешно обновленных велосипедов
    const updatedBikeIds = bikeIds.slice(0, result.updatedCount);
    await sendBulkMovementWebhook(updatedBikeIds, 'unblock', {
      movement_blocked: {
        previous: true,
        current: false
      }
    });
  } else {
    console.error('Ошибка при массовой разблокировке:', result.errors);
    toast.error('Ошибка разблокировки велосипедов');
  }

  return result;
};

/**
 * Массовая установка ограничения скорости
 */
export const bulkSetSpeedLimit = async (bikeIds: string[], speedLimit: number): Promise<BulkOperationResult> => {
  console.log('=== МАССОВАЯ УСТАНОВКА ОГРАНИЧЕНИЯ СКОРОСТИ ===');
  
  const result = await bulkUpdateBikeMovement(bikeIds, {
    speed_limit: speedLimit,
    movement_blocked: false
  });

  if (result.success && result.updatedCount > 0) {
    toast.success(`Установлено ограничение скорости ${speedLimit} км/ч для ${result.updatedCount} велосипедов`);
    
    // Отправляем вебхуки
    await sendBulkMovementWebhook(bikeIds.slice(0, result.updatedCount), 'speed_limit', {
      speed_limit: {
        previous: null,
        current: speedLimit
      },
      movement_blocked: {
        previous: true,
        current: false
      }
    });
  } else {
    toast.error('Ошибка установки ограничения скорости');
  }

  return result;
};

/**
 * Массовый сброс всех ограничений
 */
export const bulkResetRestrictions = async (bikeIds: string[]): Promise<BulkOperationResult> => {
  console.log('=== МАССОВЫЙ СБРОС ОГРАНИЧЕНИЙ ===');
  
  const result = await bulkUpdateBikeMovement(bikeIds, {
    movement_blocked: false,
    speed_limit: null
  });

  if (result.success && result.updatedCount > 0) {
    toast.success(`Сброшены ограничения для ${result.updatedCount} велосипедов`);
    
    // Отправляем вебхуки
    await sendBulkMovementWebhook(bikeIds.slice(0, result.updatedCount), 'reset', {
      movement_blocked: {
        previous: true,
        current: false
      },
      speed_limit: {
        previous: null,
        current: null
      }
    });
  } else {
    toast.error('Ошибка сброса ограничений');
  }

  return result;
};
